import React, { useState, useEffect } from 'react';
import { fetchPrizeTiers, QUIZ_CONFIG } from './config/prizeConfig';
import KBCUserProfile from './KBCUserProfile';
import type { PrizeTier } from './types';

interface RulesScreenProps {
  onStart: () => void;
  onBack: () => void;
  audioEnabled: boolean;
  onToggleAudio: () => void;
  hasAttempted?: boolean;
}

const RulesScreen: React.FC<RulesScreenProps> = ({ 
  onStart, 
  onBack, 
  audioEnabled, 
  onToggleAudio,
  hasAttempted = false
}) => {
  // Fetch prize tiers from API on mount
  const [prizeTiers, setPrizeTiers] = useState<PrizeTier[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch prize config from backend API on mount
  useEffect(() => {
    const loadPrizes = async () => {
      try {
        const tiers = await fetchPrizeTiers();
        setPrizeTiers(tiers);
      } catch (error) {
        console.error('Failed to load prize tiers:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPrizes();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-kbc-dark to-kbc-dark-accent p-4 overflow-y-auto relative">
      {/* User Profile - Top Left (Small) */}
      <KBCUserProfile size="small" position="top-left" />
      <div className="max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {/* 📋 */}
            Rules & Prizes
          </h1>
          <p className="text-white/80">Know the game before you play!</p>
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="text-center text-white/80 py-8">
            Loading prizes...
          </div>
        )}

        {/* Rules Cards */}
        <div className="space-y-4 mb-8">
          {/* Rule 1 */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📝</span>
              <div>
                <h3 className="text-lg font-bold text-kbc-gold mb-1">
                  {QUIZ_CONFIG.totalQuestions} Questions
                </h3>
                <p className="text-white/90">
                  Answer progressive questions about GenAI and Shipment CoCaptain. 
                  Each correct answer takes you closer to the next prize level.
                </p>
              </div>
            </div>
          </div>

          {/* Rule 2 */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💰</span>
              <div>
                <h3 className="text-lg font-bold text-kbc-gold mb-1">
                  Cash Out or Risk It
                </h3>
                <p className="text-white/90">
                  At certain levels, you can cash out and claim your prize, 
                  or risk it to go higher. Choose wisely!
                </p>
              </div>
            </div>
          </div>

          {/* Rule 3 */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
            <div className="flex items-start">
              <span className="text-2xl mr-3">❌</span>
              <div>
                <h3 className="text-lg font-bold text-kbc-gold mb-1">
                  One Wrong Answer = Game Over
                </h3>
                <p className="text-white/90">
                  Get one question wrong and it's over. Players can attempt the quiz up to 3 times from a single Maersk account. Use your attempts wisely - no lifelines, no second chances per attempt!
                </p>
              </div>
            </div>
          </div>


          {/* Rule 5 - Single Attempt */}
          {/* <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🎯</span>
              <div>
                <h3 className="text-lg font-bold text-kbc-gold mb-1">
                  One Shot Only
                </h3>
                <p className="text-white/90">
                  You can only play once. Your first attempt is final and will appear on the leaderboard. Proceed carefully.
                </p>
              </div>
            </div>
          </div> */}


          {/* Rule 4 */}
          {/* <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🏆</span>
              <div>
                <h3 className="text-lg font-bold text-kbc-gold mb-1">
                  Leaderboard Glory
                </h3>
                <p className="text-white/90">
                  Your score and prize will be displayed on the live leaderboard. 
                  Compete for the top spot - top 3 positions stand to win extra goodies!
                </p>
              </div>
            </div>
          </div> */}

          {/* Rule 6 - Speed Matters */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⚡</span>
              <div>
                <h3 className="text-lg font-bold text-kbc-gold mb-1">
                  Fastest Fingers First
                </h3>
                <p className="text-white/90">
                  When players have the same score, lesser completion time ranks higher. Fastest players to answer all the questions stand to win the grand prize - so every second counts!
                </p>
              </div>
            </div>
          </div>

          {/* A lot of empty space */}
          {/* <div className="h-4" />
          <div className="h-4" /> */}

          {/* Audio Toggle */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-start">
                <span className="text-2xl mr-3">🔊</span>
                <div>
                  <h3 className="text-lg font-bold text-kbc-gold mb-1">
                    Sound Effects
                  </h3>
                  <p className="text-white/90 text-sm">
                    Toggle audio for the full KBC experience
                  </p>
                </div>
              </div>
              <button
                onClick={onToggleAudio}
                className={`px-4 py-2 rounded-lg font-semibold transition ${
                  audioEnabled
                    ? 'bg-kbc-success text-white'
                    : 'bg-white/20 text-white/60'
                }`}
              >
                {audioEnabled ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
        </div>

        {/* Prize Tiers */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 mb-8">
          <h3 className="text-xl font-bold text-kbc-gold mb-4 text-center">
            Available Prizes
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {prizeTiers.map((prize) => (
              <div
                key={prize.level}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  prize.enabled
                    ? 'bg-kbc-gold/20 border border-kbc-gold/40'
                    : 'bg-white/5 border border-white/10 opacity-50'
                }`}
              >
                <div className="flex items-center">
                  <span className="text-kbc-gold font-bold mr-3 text-lg">
                    Tier {prize.level}
                  </span>
                  <span className="text-white font-medium">
                    {prize.label}
                  </span>
                </div>
                {!prize.enabled && (
                  <span className="text-xs text-white/60">
                    (unavailable)
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {!hasAttempted ? (
            <button
              onClick={onStart}
              className="w-full bg-maersk-blue hover:bg-opacity-80 text-maersk-navy font-bold text-xl py-4 rounded-lg shadow-lg transform transition hover:scale-105 active:scale-95"
            >
              🚀 Let's Go!
            </button>
          ) : (
            <div className="w-full bg-white/10 text-white/70 font-medium text-sm py-4 px-6 rounded-lg border border-white/20 text-center">
              Attempt already used.
            </div>
          )}
          
          <button
            onClick={onBack}
            className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 rounded-lg border border-white/30 transition"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default RulesScreen;
